import {useEffect, useRef, useState} from 'react';
import {checkAuthReachable} from './authAtoms';
import {withTimeout} from '@/lib/withTimeout';

// Same shape as useOfflineSync.ts's own AUTH_BACKOFF_* constants (see
// offline_sync_auth_reachability_backoff_2026_09_04.md) — kept as a
// separate copy rather than importing that file's, so this hook has no
// dependency on the mutation-replay hook's internals and can be used
// anywhere a query needs to wait on the same signal.
const AUTH_CHECK_TIMEOUT_MS = 8000;
const AUTH_BACKOFF_BASE_MS = 10000;
const AUTH_BACKOFF_MULTIPLIER = 2;
const AUTH_BACKOFF_MAX_MS = 5 * 60 * 1000; // 5 minutes
// How often to re-evaluate a pending backoff window even though nothing
// else changed — mirrors useOfflineSync.ts's SYNC_POLL_INTERVAL_MS /
// pollTick pattern (a plain "on `isOnline`" effect has nothing to
// re-trigger it once the backoff's `nextAttemptAt` has simply elapsed).
const RECHECK_TICK_MS = 5000;

/**
 * Whether Cognito itself (not just the app's own-origin reachability
 * probe in lib/onlineState.ts) is actually reachable right now — the
 * same distinction useOfflineSync.ts's mutation-replay path already
 * makes before resending queued mutations, extracted here so a plain
 * background useQuery (see useRunTicketReconciliation.ts) can gate on
 * it too.
 *
 * ADDED 2026-09-10, follow-up to offline_sync_auth_reachability_backoff_2026_09_04.md:
 * that fix stopped the OFFLINE MUTATION QUEUE from replaying into a
 * not-yet-reachable Cognito on reconnect, but useRunTicketReconciliation.ts's
 * two 30s-polling read queries (RunTicket_AllWhere, DailyWellReport_AllWhere)
 * were never covered by it — their `skip` only checked `isAuthed` (a
 * cached/local session flag) and never `isOnline` at all, so the poll
 * kept ticking (and Apollo kept sending the network leg — see
 * apolloClient.ts's timeoutLink comment: "a watchQuery's network leg
 * fires unconditionally regardless of app-level online state") straight
 * through being offline, and resumed the instant `isOnline` flipped
 * true with no confirmation Cognito itself was back yet. That's the
 * residual DailyWellReport_AllWhere 400s Duane was still seeing after
 * the mutation-queue fix.
 *
 * Starts optimistic (`true`) whenever the caller is already online on
 * first mount — the common case, and no reason to delay this hook's
 * very first fetch behind an extra round trip when nothing has actually
 * gone wrong yet. Only flips to `false` (and starts the backoff-gated
 * recheck loop) once `isOnline` itself goes false — i.e. after a real
 * disconnect — so it never resumes polling on reconnect until a fresh
 * checkAuthReachable() call has actually succeeded.
 */
export function useCognitoReachable(isOnline: boolean): boolean {
   const [reachable, setReachable] = useState(isOnline);
   const backoffRef = useRef({nextAttemptAt: 0, failureCount: 0});
   const checkingRef = useRef(false);
   const unmountedRef = useRef(false);
   const [tick, setTick] = useState(0);

   useEffect(() => {
      unmountedRef.current = false;
      return () => {
         unmountedRef.current = true;
      };
   }, []);

   // Cheap re-evaluation tick — see RECHECK_TICK_MS's own comment above.
   // Only needed while a recheck is actually pending (online but not yet
   // confirmed reachable). Ticking forever re-rendered every consumer of this
   // hook every 5 seconds even when nothing was wrong.
   useEffect(() => {
      if (!isOnline || reachable) return;
      const interval = setInterval(() => setTick((t) => t + 1), RECHECK_TICK_MS);
      return () => clearInterval(interval);
   }, [isOnline, reachable]);

   useEffect(() => {
      if (!isOnline) {
         // Ground truth just changed out from under us — stop treating
         // Cognito as reachable and reset the backoff so the NEXT
         // reconnect starts its own fresh check instead of picking up
         // wherever a prior failing streak left off.
         setReachable(false);
         backoffRef.current = {nextAttemptAt: 0, failureCount: 0};
         return;
      }
      if (reachable || checkingRef.current) return;
      if (Date.now() < backoffRef.current.nextAttemptAt) return;

      let cancelled = false;
      checkingRef.current = true;
      (async () => {
         const ok = await withTimeout(checkAuthReachable(), AUTH_CHECK_TIMEOUT_MS, () => {
            console.warn(
               `useCognitoReachable: checkAuthReachable() hadn't finished after ${AUTH_CHECK_TIMEOUT_MS}ms — treating as unreachable this pass`,
            );
         });
         checkingRef.current = false;
         if (cancelled || unmountedRef.current) return;
         if (ok) {
            backoffRef.current = {nextAttemptAt: 0, failureCount: 0};
            setReachable(true);
         } else {
            const {failureCount} = backoffRef.current;
            const delay = Math.min(AUTH_BACKOFF_BASE_MS * AUTH_BACKOFF_MULTIPLIER ** failureCount, AUTH_BACKOFF_MAX_MS);
            backoffRef.current = {nextAttemptAt: Date.now() + delay, failureCount: failureCount + 1};
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [isOnline, reachable, tick]);

   return reachable;
}
